import { useEffect, useMemo } from "react";
import { useAppDispatch } from "../../../app/hooks";
import {
    useGetUserInfoQuery,
    useSetUserCountryMutation,
} from "../../../app/rootReducer";
import { ProductTypeEnum } from "../../../common/enums/ProductTypeEnum";
import { useGetProductCategoriesQuery } from "../../../common/api/product";
import { buildProductRangeOptionsQuery } from "../../../common/utils/productRange/buildProductRangeOptionsQuery";
import { isLeafCategory } from "../../../common/utils/productRange/isLeafCategory";
import { ProductRangeCategoryInterface } from "../../../common/utils/productRange/mergeCategoriesWithProducts";
import { ProductCategoryInterface } from "../../../common/interfaces/apiV2/shared/ProductCategoryInterface";
import { SelectedFilterInterface } from "../../../common/interfaces/productRange/SelectedFilterInterface";
import { openCategory } from "../ProductRangeSlice";

interface UseProductRangeDataParams {
    productType: ProductTypeEnum[];
    selectedFilters: Record<number, SelectedFilterInterface>;
}

interface UseProductRangeDataResult {
    categories: ProductRangeCategoryInterface[];
    isCategoriesLoading: boolean;
    isCategoriesFetching: boolean;
    isCountryUpdating: boolean;
}

const mapCategoryTree = (
    categories: ProductCategoryInterface[],
): ProductRangeCategoryInterface[] =>
    categories.map((category) => ({
        ...category,
        products: [],
        children: mapCategoryTree(category.children),
    }));

export const useProductRangeData = ({
    productType,
    selectedFilters,
}: UseProductRangeDataParams): UseProductRangeDataResult => {
    const dispatch = useAppDispatch();
    const optionsQuery = buildProductRangeOptionsQuery(selectedFilters);
    const { data: userData } = useGetUserInfoQuery();
    const countryId = userData?.activeCountry?.id;
    const [, { isLoading: isCountryUpdating }] = useSetUserCountryMutation();

    const {
        data: categoriesResponse,
        isFetching: isCategoriesFetching,
        isLoading: isCategoriesLoading,
    } = useGetProductCategoriesQuery(
        {
            productType: productType[0],
            options: optionsQuery,
            countryId: countryId ?? 0,
        },
        { skip: !countryId },
    );

    useEffect(() => {
        if (!categoriesResponse?.data || categoriesResponse.data.length !== 1) {
            return;
        }

        const rootCategory = categoriesResponse.data[0];

        if (isLeafCategory(rootCategory)) {
            dispatch(openCategory(rootCategory.id));
        }
    }, [categoriesResponse?.data, dispatch, productType]);

    const categories = useMemo((): ProductRangeCategoryInterface[] => {
        if (!categoriesResponse?.data) {
            return [];
        }

        return mapCategoryTree(categoriesResponse.data);
    }, [categoriesResponse?.data]);

    return {
        categories,
        isCategoriesLoading,
        isCategoriesFetching,
        isCountryUpdating,
    };
};
